import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

/**
 * AuthCallback Component
 * Handles the OAuth redirect from Emergent Auth service.
 * Processes session_id from URL fragment and exchanges it for user session.
 * 
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export const AuthCallback = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const hasProcessed = useRef(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Prevent double processing in StrictMode
        if (hasProcessed.current) return;
        hasProcessed.current = true;

        const processAuth = async () => {
            try {
                // Extract session_id from URL fragment
                const hash = location.hash;
                const sessionIdMatch = hash.match(/session_id=([^&]+)/);
                
                if (!sessionIdMatch) {
                    throw new Error("No session_id found in URL");
                }

                const sessionId = sessionIdMatch[1];

                // Exchange session_id for user data via backend
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                const response = await fetch(`${backendUrl}/api/auth/session`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: "include",
                    body: JSON.stringify({ session_id: sessionId }),
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.detail || "Authentication failed");
                }

                const userData = await response.json();

                // Clear the hash from URL and navigate to main app with user data
                window.history.replaceState(null, "", window.location.pathname);
                
                // Navigate to sanctuary (marketplace) with user data
                navigate("/sanctuary", { 
                    state: { user: userData },
                    replace: true 
                });

            } catch (err) {
                console.error("Auth error:", err);
                setError(err.message);
                // After error, redirect back to welcome after delay
                setTimeout(() => {
                    navigate("/", { replace: true });
                }, 3000);
            }
        };

        processAuth();
    }, [location, navigate]);

    if (error) {
        return (
            <div className="min-h-screen bg-[#050505] flex items-center justify-center">
                <div className="text-center">
                    <p className="text-red-400 font-inter mb-4">{error}</p>
                    <p className="text-[rgba(212,175,55,0.5)] text-sm font-inter">
                        Returning to the threshold...
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#050505] flex items-center justify-center">
            <div className="text-center">
                <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-[rgba(212,175,55,0.7)] text-sm font-inter italic">
                    aligning the stars...
                </p>
            </div>
        </div>
    );
};

export default AuthCallback;
